import type { ExportConfig, GeneratedFile, LanguageGenerator } from './types';
import { generateEnv, toPascal } from './utils';

function genConfig(config: ExportConfig): string {
  const appEntries = config.apps
    .map((a) => `  ${a.key}: { fromEmail: '${a.fromEmail}', keyEnv: '${a.envVarName}' }`)
    .join(',\n');

  const templateEntries = config.apps
    .flatMap((a) =>
      a.templates.map((t) => {
        const schemaDoc = t.schema
          ? `\n    // Payload: ${t.schema.map((f) => f.key + (f.required ? '' : '?')).join(', ')}`
          : '';
        return `  ${t.camelName}: {${schemaDoc}\n    app: '${a.key}',\n    slug: '${t.slug}',\n  }`;
      }),
    )
    .join(',\n\n');

  return `// mail.config.js — generated by Mail Service SDK Export

/** @type {Record<string, { fromEmail: string; keyEnv: string }>} */
const MAIL_APPS = {
${appEntries},
};

/** @type {Record<string, { app: string; slug: string }>} */
const MAIL_TEMPLATES = {
${templateEntries},
};

module.exports = { MAIL_APPS, MAIL_TEMPLATES };
`;
}

function genService(config: ExportConfig): string {
  const typedHelpers = config.apps
    .flatMap((a) =>
      a.templates.map((t) => {
        const methodName = `send${toPascal(t.camelName)}`;
        const paramDocs = t.schema
          ? `\n   * @param {Object} data${t.schema.map((f) => `\n   * @param {${f.type}} ${f.required ? '' : '['}data.${f.key}${f.required ? '' : ']'}`).join('')}`
          : '';
        return `  /**\n   * @param {string} recipient${paramDocs}\n   */\n  static ${methodName}(recipient, data = {}) {\n    return this.send('${t.camelName}', recipient, data);\n  }`;
      }),
    )
    .join('\n\n');

  return `// mail.service.js — generated by Mail Service SDK Export
const { MAIL_APPS, MAIL_TEMPLATES } = require('./mail.config');

const SERVICE_URL = (process.env.${config.serviceUrlVarName} || '').replace(/\\/$/, '');

function getKey(app) { return process.env[MAIL_APPS[app].keyEnv] || ''; }
function headers(app) { return { 'Content-Type': 'application/json', 'X-API-KEY': getKey(app) }; }
function isReady(app) { return !!(SERVICE_URL && getKey(app)); }

async function post(app, path, body) {
  if (!isReady(app)) {
    console.warn(\`[Mail] App "\${app}" not configured\`);
    return { success: false, error: 'Not configured' };
  }
  try {
    const res = await fetch(\`\${SERVICE_URL}\${path}\`, {
      method: 'POST',
      headers: headers(app),
      body: JSON.stringify(body),
    });
    return await res.json();
  } catch (err) {
    return { success: false, error: err.message };
  }
}

class MailService {
  static send(template, recipient, data = {}) {
    const t = MAIL_TEMPLATES[template];
    return post(t.app, '/v1/send', { template_slug: t.slug, recipient, data });
  }

  static sendRaw(app, { subject, html, recipient, fromName }) {
    return post(app, '/v1/send/raw', { subject, html, recipient, from_name: fromName });
  }

${typedHelpers}
}

module.exports = { MailService };
`;
}

export const javascriptGenerator: LanguageGenerator = {
  id: 'javascript',
  name: 'JavaScript',
  category: 'full-sdk',
  color: '#F7DF1E',
  generate(config: ExportConfig): GeneratedFile[] {
    return [
      { filename: 'mail.config.js', content: genConfig(config), syntax: 'javascript' },
      { filename: 'mail.service.js', content: genService(config), syntax: 'javascript' },
      { filename: '.env', content: generateEnv(config), syntax: 'shell' },
    ];
  },
};
